
export interface WildlifeObs {
  id: string;
  species: string;
  emoji: string;
  category: 'Mammal' | 'Bird';
  location: { latitude: number; longitude: number };
  observedAt: string | null;
  locality?: string | null;
  municipality?: string | null;
  thumbnailUrl?: string | null;
  detailUrl?: string | null;
}

export const SPECIES_EMOJI: Record<string, string> = {
  Elg: '🦌', Hjort: '🦌', Radyr: '🦌', Rein: '🦌',
  Rev: '🦊', Grevling: '🦡', Oter: '🦦', Bever: '🦫',
  Havorn: '🦅', Kongeorn: '🦅', Ugle: '🦉', Tiur: '🐦', Orrfugl: '🐦', Trane: '🕊️'
};

export const fetchWildlifeForRoute = async (pathCoords: { latitude: number; longitude: number }[]): Promise<WildlifeObs[]> => {
  if (pathCoords.length < 2) return [];
  const r = 6378137;
  const toWM = (lat: number, lon: number) => ({
    x: (lon * Math.PI / 180) * r,
    y: Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 360))) * r
  });
  const projected = pathCoords
    .filter(p => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
    .map(p => toWM(p.latitude, p.longitude));
  if (!projected.length) return [];

  let minX = projected[0].x, maxX = projected[0].x;
  let minY = projected[0].y, maxY = projected[0].y;
  projected.forEach(p => {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  });
  const pad = 400;
  const wkt = `POLYGON((${minX - pad} ${minY - pad},${maxX + pad} ${minY - pad},${maxX + pad} ${maxY + pad},${minX - pad} ${maxY + pad},${minX - pad} ${minY - pad}))`;

  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 12);

  const params = new URLSearchParams({
    pageIndex: '0', pageSize: '600',
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
    gmWktPolygon: wkt
  });
  const sourceUrl = `https://artskart.artsdatabanken.no/publicapi/api/observations/list?${params}`;
  const proxyUrl = `https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy?url=${encodeURIComponent(sourceUrl)}`;

  const response = await fetch(proxyUrl, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`wildlife_${response.status}`);
  const json = await response.json();

  const rawList: any[] = Array.isArray(json) ? json
    : Array.isArray(json?.data) ? json.data
      : Array.isArray(json?.Observations) ? json.Observations
        : Array.isArray(json?.results) ? json.results
          : Array.isArray(json?.observations) ? json.observations : [];

  const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const getSpecies = (obs: any): { species: string; category: 'Mammal' | 'Bird' } | null => {
    const names = [obs?.Name, obs?.vernacularName, obs?.vernacularNameNb, obs?.vernacularNameBokmal,
    obs?.taxonVernacularName, obs?.taxonName, obs?.speciesName, obs?.ScientificName, obs?.scientificName,
    obs?.taxon?.vernacularName, obs?.taxon?.scientificName].filter(Boolean).map((v: any) => norm(String(v)));
    const has = (s: string) => names.some(n => n.includes(s));
    if (has('elg') || has('alces alces')) return { species: 'Elg', category: 'Mammal' };
    if (has('hjort') || has('cervus elaphus')) return { species: 'Hjort', category: 'Mammal' };
    if (has('radyr') || has('capreolus')) return { species: 'Radyr', category: 'Mammal' };
    if (has('rein') || has('rangifer')) return { species: 'Rein', category: 'Mammal' };
    if (has('rev') || has('vulpes')) return { species: 'Rev', category: 'Mammal' };
    if (has('grevling') || has('meles')) return { species: 'Grevling', category: 'Mammal' };
    if (has('oter') || has('lutra')) return { species: 'Oter', category: 'Mammal' };
    if (has('bever') || has('castor')) return { species: 'Bever', category: 'Mammal' };
    if (has('havorn') || has('haliaeetus')) return { species: 'Havorn', category: 'Bird' };
    if (has('kongeorn') || has('aquila')) return { species: 'Kongeorn', category: 'Bird' };
    if (has('ugle') || has('strigidae')) return { species: 'Ugle', category: 'Bird' };
    if (has('tiur') || has('tetrao urogallus')) return { species: 'Tiur', category: 'Bird' };
    if (has('orrfugl') || has('lyrurus')) return { species: 'Orrfugl', category: 'Bird' };
    if (has('trane') || has('grus grus')) return { species: 'Trane', category: 'Bird' };
    return null;
  };

  // Artsdatabanken returns coordinates as strings with comma decimal ("60,128")
  const toNum = (v: unknown): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') { const n = Number(v.replace(',', '.').trim()); return Number.isFinite(n) ? n : NaN; }
    return NaN;
  };

  const parseLoc = (obs: any) => {
    const lat = toNum(obs?.decimalLatitude ?? obs?.lat ?? obs?.latitude ?? obs?.Latitude);
    const lon = toNum(obs?.decimalLongitude ?? obs?.lon ?? obs?.lng ?? obs?.long ?? obs?.longitude ?? obs?.Longitude);
    if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
    return { latitude: lat, longitude: lon };
  };

  const parseDate = (obs: any): string | null => {
    const raw = obs?.TrackDateTime || obs?.CollectedDate || obs?.observedAt || obs?.observationDate || obs?.eventDate || obs?.dateObserved || obs?.createdAt || obs?.date;
    if (!raw) return null;
    const d = new Date(String(raw).includes('.') && !String(raw).includes('T')
      ? String(raw).split('.').reverse().join('-')  // "14.08.2025" → "2025-08-14"
      : raw);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  const parseThumbnail = (obs: any): string | null => {
    const list = obs?.ThumbImgUrls;
    if (Array.isArray(list) && list.length > 0) {
      const first = list.find((v: any) => typeof v === 'string' && v.trim().length > 0);
      return first || null;
    }
    if (typeof list === 'string' && list.trim().length > 0) return list;
    return null;
  };

  const results: WildlifeObs[] = [];
  const seen = new Set<string>();
  rawList.forEach((obs, idx) => {
    const sp = getSpecies(obs);
    const loc = parseLoc(obs);
    if (!sp || !loc) return;
    const key = `${sp.species}-${loc.latitude.toFixed(3)}-${loc.longitude.toFixed(3)}`;
    if (seen.has(key)) return;
    seen.add(key);
    results.push({
      id: String(obs?.Id || obs?.id || `w_${idx}`),
      species: sp.species,
      emoji: SPECIES_EMOJI[sp.species] || '🐾',
      category: sp.category,
      location: loc,
      observedAt: parseDate(obs),
      locality: typeof obs?.Locality === 'string' ? obs.Locality : null,
      municipality: typeof obs?.Municipality === 'string' ? obs.Municipality : null,
      thumbnailUrl: parseThumbnail(obs),
      detailUrl: typeof obs?.DetailUrl === 'string' ? obs.DetailUrl : (typeof obs?.ObsUrl === 'string' ? obs.ObsUrl : null)
    });
  });
  return results;
};
